import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { PropTypes } from 'prop-types';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { Table, Row, Col, Button } from 'antd';
import InvoicePicklistReportActions from '../../Stores/InvoicePicklistReport/Actions';

class ReportTable extends React.PureComponent {
  constructor() {
    super();

    this.useOnDownloadExcel = this.useOnDownloadExcel.bind(this);

    this.handleTableChange = this.handleTableChange.bind(this);
  }

  handleTableChange(pagination, filters, sorter) {
    const { criteria, fetchReport } = this.props;

    const activeSorter = Array.isArray(sorter) ? sorter : [sorter];

    const sorts = activeSorter
      .filter(entry => entry && entry.order)
      .map(entry => ({
        field: entry.field,
        direction: entry.order === 'ascend' ? 'asc' : 'desc'
      }))
      .filter(entry => ['sls_inv_code', 'sls_inv_doc_date'].includes(entry.field));

    fetchReport(criteria, sorts, pagination.pageSize, pagination.current);
  }

  useOnDownloadExcel() {
    const { criteria, exportReport } = this.props;

    exportReport(criteria);
  }

  render() {
    const {
      intl,
      sorts,
      criteria,
      pagination,
      reportData,
      reportIsLoading,
      exportIsLoading
    } = this.props;

    const getSortOrder = field => {
      const sort = sorts.find(entry => entry.field === field);

      if (!sort) {
        return null;
      }

      return sort.direction === 'asc' ? 'ascend' : 'descend';
    };

    const columns = [
      {
        title: intl.formatMessage({
          id: 'invoice_doc_code'
        }),
        dataIndex: 'sls_inv_code',
        key: 'sls_inv_code',
        sorter: true,
        sortOrder: getSortOrder('sls_inv_code')
      },
      {
        title: intl.formatMessage({
          id: 'invoice_doc_date'
        }),
        dataIndex: 'sls_inv_doc_date',
        key: 'sls_inv_doc_date',
        sorter: true,
        sortOrder: getSortOrder('sls_inv_doc_date')
      },
      {
        title: intl.formatMessage({
          id: 'pick_list_code'
        }),
        dataIndex: 'pick_list_code',
        key: 'pick_list_code'
      },
      {
        title: intl.formatMessage({
          id: 'pick_list_date'
        }),
        dataIndex: 'pick_list_date',
        key: 'pick_list_date'
      },
      {
        title: intl.formatMessage({
          id: 'pick_list_date_time'
        }),
        dataIndex: 'pick_list_date_time',
        key: 'pick_list_date_time'
      }
    ];

    const itemColumns = [
      {
        title: intl.formatMessage({ id: 'item_code' }),
        dataIndex: 'item_code',
        key: 'item_code'
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'desc_01',
        key: 'desc_01'
      },
      {
        title: intl.formatMessage({ id: 'batch_serial_no' }),
        dataIndex: 'batch_serial_no',
        key: 'batch_serial_no'
      },
      {
        title: `${intl.formatMessage({ id: 'case' })} ${intl.formatMessage({ id: 'qty' })}`,
        dataIndex: 'case_qty',
        key: 'case_qty',
        align: 'right'
      },
      {
        title: intl.formatMessage({
          id: 'case_uom'
        }),
        dataIndex: 'case_uom',
        key: 'case_uom',
        align: 'right'
      },
      {
        title: intl.formatMessage({
          id: 'loose_qty'
        }),
        dataIndex: 'loose_qty',
        key: 'loose_qty',
        align: 'right'
      },
      {
        title: intl.formatMessage({
          id: 'loose_uom'
        }),
        dataIndex: 'loose_uom',
        key: 'loose_uom',
        align: 'right'
      }
    ];

    return (
      <Table
        size="small"
        rowKey={record => `${record.sls_inv_code}-${record.pick_list_code}`}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
        }}
        columns={columns}
        dataSource={reportData}
        loading={reportIsLoading}
        onChange={this.handleTableChange}
        bordered
        title={() => (
          <Row type="flex" justify="space-between" gutter={[0, 16]}>
            <Col span={8}>
              <Button
                type="primary"
                loading={exportIsLoading}
                disabled={reportIsLoading || (!criteria.doc_code && !criteria.doc_date)}
                onClick={this.useOnDownloadExcel}
                icon={<CloudDownloadOutlined />}
              >
                {intl.formatMessage({
                  id: 'excel'
                })}
              </Button>
            </Col>
          </Row>
        )}
        expandedRowRender={record => (
          <Table
            size="small"
            rowKey={(item, index) => `${record.pick_list_code}-${item.item_code}-${index}`}
            columns={itemColumns}
            dataSource={record.pick_list_items || []}
            pagination={false}
            bordered
          />
        )}
        scroll={{ x: 950 }}
      />
    );
  }
}

ReportTable.propTypes = {
  intl: PropTypes.object,
  criteria: PropTypes.object,
  sorts: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.object,
  reportData: PropTypes.arrayOf(PropTypes.object),
  reportIsLoading: PropTypes.bool,
  exportIsLoading: PropTypes.bool,
  fetchReport: PropTypes.func,
  exportReport: PropTypes.func
};

ReportTable.defaultProps = {
  intl: {},
  criteria: {},
  sorts: [],

  pagination: {
    current: 1,
    pageSize: 20,
    total: 0
  },

  reportData: [],
  reportIsLoading: false,
  exportIsLoading: false,

  fetchReport() {},
  exportReport() {}
};

const mapStateToProps = state => ({
  criteria: state.invoicePicklistReport.criteria,

  sorts: state.invoicePicklistReport.sorts,

  pagination: state.invoicePicklistReport.pagination,

  reportData: state.invoicePicklistReport.reportData,

  reportIsLoading: state.invoicePicklistReport.reportIsLoading,

  exportIsLoading: state.invoicePicklistReport.exportIsLoading
});

const mapDispatchToProps = dispatch => ({
  fetchReport: (criteria, sorts, pageSize, page) =>
    dispatch(
      InvoicePicklistReportActions.invoicePicklistReportFetch(criteria, sorts, pageSize, page)
    ),

  exportReport: criteria =>
    dispatch(InvoicePicklistReportActions.invoicePicklistReportExport(criteria))
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ReportTable));
